import React, { useState, useContext, useEffect } from "react";
import { GoSearch } from "react-icons/go";
import {
  AiOutlineShoppingCart,
  AiOutlineUser,
  AiOutlineHeart,
  AiOutlineClose,
  AiOutlineHome,
  AiFillStar,
} from "react-icons/ai";
import {
  MdOutlineCardGiftcard,
  MdLocationOn,
  MdMenu,
  MdOutlineSettings,
  MdOutlineLocalOffer,
} from "react-icons/md";
import { BiChevronDown, BiChevronRight } from "react-icons/bi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import https from "../../utils/https";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { AuthContext } from "../../context/AuthContext";
import CategoryList from "../home/CategoryList";
import WishlistModal from "./WishlistModal";
import CartModal from "./CartModal";
import AccountModal from "./AccountModal";
import OrdersModal from "./OrdersModal";
import SettingsModal from "./SettingsModal";
import AddressModal from "./AddressModal";
import { fetchCartItems } from "../../utils/cartSync";
import { getWishlist } from "../../utils/wishlistSync";
import { fetchAddresses } from "../../store/slices/addressSlice";
import { toast } from "../../utils/toast";

const Header = () => {
  const { isAuth } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const reduxUser = useSelector((state: RootState) => state.auth.user);
  const { products } = useSelector((state: RootState) => state.product);

  // Fallbacks matching WishlistModal and CartModal drawers
  const [wishlistCount, setWishlistCount] = useState(0);
  const [cartCount, setCartCount] = useState(0);
  const [cartTotal, setCartTotal] = useState(0);

  const updateWishlistCount = () => {
    try {
      setWishlistCount(getWishlist().length);
    } catch (e) {
      console.error("Error updating wishlist count:", e);
    }
  };

  const updateCartCount = async (useLocalStorageOnly = false) => {
    try {
      if (useLocalStorageOnly) {
        const cartStr = localStorage.getItem("honeywill_cart");
        const cart = cartStr ? JSON.parse(cartStr) : [];
        setCartCount(cart.length);
        const total = cart.reduce((acc: number, item: any) => acc + (item.subtotal || 0), 0);
        setCartTotal(total);
        return;
      }
      const cart = await fetchCartItems();
      setCartCount(cart.length);
      const total = cart.reduce((acc: number, item: any) => acc + (item.subtotal || 0), 0);
      setCartTotal(total);
    } catch (e) {
      console.error("Error updating cart count:", e);
    }
  };

  useEffect(() => {
    updateCartCount(false);
    updateWishlistCount();

    const handleCartUpdate = () => {
      updateCartCount(true);
    };

    window.addEventListener("cartUpdated", handleCartUpdate);
    window.addEventListener("wishlistUpdated", updateWishlistCount);
    window.addEventListener("storage", handleCartUpdate);
    return () => {
      window.removeEventListener("cartUpdated", handleCartUpdate);
      window.removeEventListener("wishlistUpdated", updateWishlistCount);
      window.removeEventListener("storage", handleCartUpdate);
    };
  }, [isAuth]);

  // Build name object from Redux user for backwards compatibility
  const name = reduxUser
    ? {
        firstname: reduxUser.first_name || reduxUser.firstname || "",
        lastname: reduxUser.last_name || reduxUser.lastname || "",
        email: reduxUser.email || reduxUser.username || "",
        phone: reduxUser.phone_number || "",
      }
    : {};

  const dispatch = useDispatch<AppDispatch>();
  const { addresses } = useSelector((state: RootState) => state.address);
  const defaultAddress = addresses.find((a) => a.isDefault) || addresses[0];

  const [isAccountOpen, setIsAccountOpen] = useState(false);
  const [isLocOpen, setIsLocOpen] = useState(false);
  const [isWishlistOpen, setIsWishlistOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [cartInitialStep, setCartInitialStep] = useState<"cart" | "checkout">(
    "cart",
  );
  const [isOrdersOpen, setIsOrdersOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (
        !target.closest(".search-container") &&
        !target.closest(".mobile-search-wrapper")
      ) {
        setShowSuggestions(false);
      }
    };
    const handleOrders = () => setIsOrdersOpen(true);
    const handleCart = () => setIsCartOpen(true);
    const handleWishlist = () => setIsWishlistOpen(true);
    const handleAccount = () => setIsAccountOpen(true);

    window.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("openOrdersModal", handleOrders);
    window.addEventListener("openCartModal", handleCart);
    window.addEventListener("openWishlistModal", handleWishlist);
    window.addEventListener("openAccountModal", handleAccount);

    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("openOrdersModal", handleOrders);
      window.removeEventListener("openCartModal", handleCart);
      window.removeEventListener("openWishlistModal", handleWishlist);
      window.removeEventListener("openAccountModal", handleAccount);
    };
  }, []);

  const handleSearchFocus = async () => {
    setShowSuggestions(true);
    if (allProducts.length === 0) {
      try {
        const res = await https.get("products?limit=1000");
        const productsArray = res.data || [];
        setAllProducts(productsArray);
        if (searchQuery.trim()) {
          filterProducts(searchQuery, productsArray);
        }
      } catch (err) {
        console.error("Error fetching all products for search:", err);
      }
    }
  };

  const filterProducts = (query: string, productsList: any[]) => {
    if (!query.trim()) {
      setSuggestions([]);
      return;
    }
    const lowerQuery = query.toLowerCase();
    const filtered = productsList.filter((p) => {
      const nameMatch = p.name?.toLowerCase().includes(lowerQuery);
      const descMatch = p.description?.toLowerCase().includes(lowerQuery);

      // Brand matching
      const brandName = typeof p.brand_id === "object" ? p.brand_id?.name : "";
      const brandMatch = brandName?.toLowerCase().includes(lowerQuery);

      // Category matching
      const catName =
        typeof p.category_id === "object" ? p.category_id?.name : "";
      const catMatch = catName?.toLowerCase().includes(lowerQuery);

      // Subcategory matching
      const subcatName =
        typeof p.subcategory_id === "object" ? p.subcategory_id?.name : "";
      const subcatMatch = subcatName?.toLowerCase().includes(lowerQuery);

      // Main category matching
      const mainCatName =
        typeof p.main_category === "object" ? p.main_category?.name : "";
      const mainCatMatch = mainCatName?.toLowerCase().includes(lowerQuery);

      return (
        nameMatch ||
        descMatch ||
        brandMatch ||
        catMatch ||
        subcatMatch ||
        mainCatMatch
      );
    });
    setSuggestions(filtered.slice(0, 10));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchQuery(val);
    filterProducts(val, allProducts);
  };

  const onLocOpen = () => setIsLocOpen(true);
  const onLocClose = () => setIsLocOpen(false);

  useEffect(() => {
    if (isAuth) {
      dispatch(fetchAddresses(undefined));
    }
  }, [isAuth, dispatch]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Check for ?wishlist=open, ?orders=open, ?cart=open, or ?settings=open on route change
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    if (queryParams.get("wishlist") === "open") {
      setIsWishlistOpen(true);
      window.history.replaceState({}, document.title, location.pathname);
    } else if (queryParams.get("orders") === "open") {
      if (isAuth) {
        setIsOrdersOpen(true);
      } else {
        setIsAccountOpen(true);
        toast.info("Please login to view your orders.");
      }
      window.history.replaceState({}, document.title, location.pathname);
    } else if (
      queryParams.get("cart") === "open" ||
      queryParams.get("cart") === "checkout"
    ) {
      setCartInitialStep(
        queryParams.get("cart") === "checkout" ? "checkout" : "cart",
      );
      setIsCartOpen(true);
      window.history.replaceState({}, document.title, location.pathname);
    } else if (queryParams.get("settings") === "open") {
      setIsSettingsOpen(true);
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location]);

  const menuStyles: React.CSSProperties = {
    position: "absolute",
    top: "100%",
    right: "0",
    backgroundColor: "white",
    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
    borderRadius: "8px",
    minWidth: "240px",
    zIndex: 1000,
    display: "none", // Not used anymore as we switched to AccountModal
    marginTop: "15px",
    border: "1px solid #f0f0f0",
    overflow: "hidden",
    animation: "fadeIn 0.2s ease-out",
  };

  const menuItemStyle: React.CSSProperties = {
    padding: "14px 20px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    cursor: "pointer",
    fontSize: "14px",
    color: "#282c3f",
    transition: "all 0.2s",
    fontWeight: "500",
  };

  return (
    <div
      style={{
        backgroundColor: "#ffffffff",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        transition: "all 0.3s",
        boxShadow: isScrolled
          ? "0 4px 20px rgba(0,0,0,0.08)"
          : "0 1px 0 rgba(0,0,0,0.05)",
      }}
    >
      <style>
        {`
                :root {
                    --color-primary: #43c8ca;
                    --color-primary-dark: #2fa8aa;
                    --color-secondary: #dbeb83;
                    --color-text: #282c3f;
                    --color-text-light: #696e79;
                    --color-border: #f0f0f0;
                    --shadow-sm: 0 2px 8px rgba(0,0,0,0.05);
                    --shadow-md: 0 4px 20px rgba(0,0,0,0.08);
                }

                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                /* ── Logo ── */
                .nav-brand { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); }
                .nav-brand:hover { transform: translateY(-1px); }

                /* ── Search ── */
                .search-container {
                    transition: all 0.3s;
                }
                .search-input {
                    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .search-input:hover { background-color: #fff; border-color: var(--color-primary); }
                .search-input:focus { 
                    background-color: #fff; 
                    box-shadow: 0 0 0 4px rgba(0,164,95,0.1); 
                    border-color: var(--color-primary); 
                    outline: none;
                    width: 100%;
                }

                /* ── Nav ── */
                .menu-item:hover { background-color: #f8fafc; color: var(--color-primary) !important; }
                .nav-link { transition: all 0.2s; }
                .nav-link:hover .nav-text, .nav-link:hover .nav-icon { color: var(--color-primary) !important; }
                
                .nav-link:active { transform: scale(0.95); }

                /* ── Hamburger Lines ── */
                .ham-line { display: block; width: 22px; height: 2px; background: #282c3f; transition: all 0.3s; }

                /* ── Drawer Overlay ── */
                .drawer-overlay {
                    display: none;
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.6);
                    backdrop-filter: blur(4px);
                    z-index: 9998;
                    animation: fadeOverlay 0.3s ease;
                }
                .drawer-overlay.open { display: block; }

                /* ── Slide-in Drawer (Bottom Sheet) ── */
                .mobile-drawer {
                    position: fixed;
                    bottom: -100%;
                    left: 0;
                    right: 0;
                    top: auto;
                    width: 100%;
                    max-height: 80dvh;
                    background: #fff;
                    z-index: 9999;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 0 -10px 30px rgba(0,0,0,0.15);
                    border-top-left-radius: 24px;
                    border-top-right-radius: 24px;
                    transition: bottom 0.4s cubic-bezier(0.4,0,0.2,1);
                    overflow-y: auto;
                }
                .mobile-drawer.open { bottom: 0; }

                @keyframes fadeOverlay {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }

                /* ── Top Bar ── */
                .announcement-bar {
                    display: flex;
                }
                @media (max-width: 900px) {
                    .announcement-bar { display: none !important; }
                }
                .top-link {
                    color: rgba(255,255,255,0.85);
                    text-decoration: none;
                    transition: color 0.2s;
                    font-size: 12px;
                    font-weight: 600;
                    letter-spacing: 0.2px;
                }
                .top-link:hover { color: #dbeb83 !important; }

                /* ── Responsive ── */
                @media (max-width: 900px) {
                    .header-main-row {
                        height: auto !important;
                        padding: 14px 0 !important;
                    }
                    .top-bar-inner {
                        padding: 0 16px !important;
                        flex-direction: column !important;
                        gap: 14px !important;
                        align-items: flex-start !important;
                    }
                    .header-logo-link {
                        justify-content: flex-start !important;
                        width: auto !important;
                    }
                    .desktop-nav-content { display: none !important; }
                    .mobile-interactive-row {
                        display: flex !important;
                        align-items: center !important;
                        gap: 16px !important;
                        width: 100% !important;
                    }
                }
                `}
      </style>

      {/* ── Top Announcement Bar ── */}
      <div
        className="announcement-bar"
        style={{
          backgroundColor: "#1a6b6d",
          height: "36px",
          alignItems: "center",
          justifyContent: "flex-end",
          padding: "0 40px",
          boxSizing: "border-box",
          fontSize: "12px",
          fontWeight: "600",
          color: "rgba(255,255,255,0.85)",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          gap: "18px",
          background: "linear-gradient(90deg, #1a6b6d 0%, #2fa8aa 100%)",
        }}
      >
        <a href="#sell" className="top-link">
          Buy Now on HoneyCool
        </a>
        <span style={{ color: "rgba(255,255,255,0.3)" }}>|</span>
        <a href="" className="top-link">
         Online Order 
        </a>
        <span style={{ color: "rgba(255,255,255,0.3)" }}>|</span>
       

        {/* Lime CTA */}
        <a
          href="tel:180030002434"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            backgroundColor: "#dbeb83",
            color: "#1a3a1a",
            padding: "5px 12px",
            borderRadius: "4px",
            textDecoration: "none",
            fontWeight: "800",
            marginLeft: "8px",
            fontSize: "11px",
            transition: "all 0.2s",
            letterSpacing: "0.2px",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#c8d866";
            e.currentTarget.style.transform = "scale(1.03)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#dbeb83";
            e.currentTarget.style.transform = "scale(1)";
          }}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
          </svg>
          <span>Missed Call To Order: +91 9881395836</span>
        </a>
      </div>

      {/* ── Main Header Row ── */}
      <div
        className="header-main-row"
        style={{
          height: isScrolled ? "92px" : "92px",
          display: "flex",
          alignItems: "center",
          transition: "all 0.3s",
          border: 0,
          borderBottom: "0px solid #000",
          //   background: "red",
        }}
      >
        <div
          className="top-bar-inner"
          style={{
            maxWidth: "1600px",
            margin: "0 auto",
            width: "100%",
            display: "flex",
            alignItems: "center",
            padding: "0 40px",
            gap: "40px",
          }}
        >
          {/* 1. Logo */}
          <Link
            to="/"
            className="header-logo-link"
            style={{
              textDecoration: "none",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
            }}
          >
            <img
              src="/honeywill.png"
              alt="HoneyCool"
              style={{
                height: "50px",
                width: "auto",
                mixBlendMode: "multiply",
              }}
            />
          </Link>

          {/* 2. Desktop Navigation Content */}
          <div
            className="desktop-nav-content"
            style={{
              display: "flex",
              flex: 1,
              alignItems: "center",
              gap: "40px",
            }}
          >
            {/* Search Bar */}
            <div
              className="search-container"
              style={{ flex: 1, position: "relative" }}
            >
              <GoSearch
                style={{
                  position: "absolute",
                  left: "18px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9aa0a6",
                  fontSize: "18px",
                  zIndex: 2,
                }}
              />
              <input
                className="search-input"
                placeholder="Search for products, brands and more..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                style={{
                  width: "100%",
                  backgroundColor: "#f0f2f59f",
                  border: "1.5px solid #75767945",
                  borderRadius: "12px",
                  fontSize: "15px",
                  fontWeight: "500",
                  height: "46px",
                  padding: "0 20px 0 52px",
                  boxSizing: "border-box",
                  color: "#1a1a2e",
                }}
              />
              {showSuggestions && searchQuery.trim().length > 0 && (
                <div
                  className="search-suggestions-dropdown"
                  style={{
                    position: "absolute",
                    top: "52px",
                    left: 0,
                    right: 0,
                    backgroundColor: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    boxShadow:
                      "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
                    maxHeight: "380px",
                    overflowY: "auto",
                    zIndex: 9999,
                    padding: "8px 0",
                  }}
                >
                  {suggestions.length > 0 ? (
                    suggestions.map((product) => (
                      <div
                        key={product._id}
                        onClick={() => {
                          navigate(`/product/${product._id}`);
                          setSearchQuery("");
                          setShowSuggestions(false);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          padding: "10px 16px",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        className="suggestion-item"
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#f8fafc")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <img
                          src={
                            product.images?.[0] ||
                            "/images/products/placeholder.png"
                          }
                          alt={product.name}
                          style={{
                            width: "40px",
                            height: "40px",
                            objectFit: "contain",
                            borderRadius: "4px",
                            border: "1px solid #f1f5f9",
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "/images/products/placeholder.png";
                          }}
                        />
                        <div
                          style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            textAlign: "left",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              color: "#1e293b",
                            }}
                          >
                            {product.name}
                          </span>
                          <span
                            style={{
                              fontSize: "11px",
                              color: "#64748b",
                              fontWeight: "500",
                            }}
                          >
                            {product.subcategory_id?.name ||
                              product.category_id?.name ||
                              "Copper Product"}
                          </span>
                        </div>
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: "800",
                            color: "#0f172a",
                          }}
                        >
                          ₹{product.price?.toLocaleString("en-IN") || "0"}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        padding: "16px",
                        color: "#64748b",
                        fontSize: "14px",
                        fontWeight: "500",
                        textAlign: "center",
                      }}
                    >
                      No products found matching "{searchQuery}"
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Actions */}
            <div
              className="actions-container"
              style={{ display: "flex", alignItems: "center", gap: "28px" }}
            >
              {/* Profile */}
              <div onClick={() => setIsAccountOpen(true)}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    cursor: "pointer",
                    padding: "8px 4px",
                  }}
                  className="nav-link"
                >
                  <div style={{ position: "relative" }}>
                    <AiOutlineUser
                      className="nav-icon"
                      style={{ fontSize: "24px", color: "#282c3f" }}
                    />
                    {isAuth && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: 0,
                          right: 0,
                          width: "10px",
                          height: "10px",
                          background: "#4caf50",
                          border: "2px solid white",
                          borderRadius: "50%",
                        }}
                      />
                    )}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span
                      className="nav-text"
                      style={{
                        fontSize: "13px",
                        fontWeight: "700",
                        color: "#282c3f",
                        lineHeight: 1,
                      }}
                    >
                      {isAuth ? name.firstname : "Account"}
                    </span>
                    <span
                      style={{
                        fontSize: "10px",
                        color: "#878787",
                        fontWeight: 600,
                      }}
                    >
                      {isAuth ? "HoneyCool User" : "Login/Sign Up"}
                    </span>
                  </div>
                  <BiChevronDown style={{ color: "#878787" }} />
                </div>
              </div>

              {/* Wishlist */}
              <div
                onClick={() => setIsWishlistOpen(true)}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "2px",
                  cursor: "pointer",
                }}
                className="nav-link"
              >
                <div
                  style={{
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <AiOutlineHeart
                    className="nav-icon"
                    style={{
                      fontSize: "24px",
                      color: "#282c3f",
                      transition: "color 0.2s",
                    }}
                  />
                  <span
                    style={{
                      position: "absolute",
                      top: "-8px",
                      right: "-12px",
                      background: "#43c8ca",
                      color: "white",
                      fontSize: "10px",
                      fontWeight: "800",
                      borderRadius: "50%",
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "2px solid white",
                    }}
                  >
                    {wishlistCount}
                  </span>
                </div>
                <span
                  className="nav-text"
                  style={{
                    fontSize: "12px",
                    fontWeight: "700",
                    color: "#282c3f",
                    transition: "color 0.2s",
                  }}
                >
                  Wishlist
                </span>
              </div>

              {/* Cart */}
              <div
                onClick={() => setIsCartOpen(true)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  ...(cartCount > 0
                    ? {
                        backgroundColor: "#16a34a",
                        color: "white",
                        padding: "8px 12px",
                        borderRadius: "8px",
                        gap: "8px",
                      }
                    : {
                        flexDirection: "column",
                        gap: "2px",
                      }),
                }}
                className={`nav-link ${cartCount > 0 ? "cart-btn-active" : ""}`}
              >
                {cartCount > 0 ? (
                  <>
                    <AiOutlineShoppingCart style={{ fontSize: "24px", color: "white" }} />
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", lineHeight: "1.2" }}>
                      <span style={{ fontSize: "12px", fontWeight: "700" }}>
                        {cartCount} {cartCount === 1 ? "item" : "items"}
                      </span>
                      <span style={{ fontSize: "14px", fontWeight: "800" }}>
                        ₹{cartTotal.toLocaleString("en-IN")}
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <AiOutlineShoppingCart
                        className="nav-icon"
                        style={{
                          fontSize: "24px",
                          color: "#282c3f",
                          transition: "color 0.2s",
                        }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          top: "-8px",
                          right: "-14px",
                          background: "var(--color-primary)",
                          color: "white",
                          fontSize: "10px",
                          fontWeight: "800",
                          borderRadius: "50%",
                          width: "20px",
                          height: "20px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "2px solid white",
                        }}
                      >
                        {cartCount}
                      </span>
                    </div>
                    <span
                      className="nav-text"
                      style={{
                        fontSize: "12px",
                        fontWeight: "700",
                        color: "#282c3f",
                        transition: "color 0.2s",
                      }}
                    >
                      Bag
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* 3. Mobile Row: Search Bar & Actions combined */}
          <div className="mobile-interactive-row" style={{ display: "none" }}>
            {/* Mobile Search Bar inside the row */}
            <div
              className="mobile-search-wrapper"
              style={{ flex: 1, position: "relative" }}
            >
              <GoSearch
                style={{
                  position: "absolute",
                  left: "14px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#9aa0a6",
                  fontSize: "16px",
                }}
              />
              <input
                placeholder="Search for products..."
                value={searchQuery}
                onChange={handleSearchChange}
                onFocus={handleSearchFocus}
                style={{
                  width: "100%",
                  backgroundColor: "#f0f2f5",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "14px",
                  height: "42px",
                  paddingLeft: "44px",
                  paddingRight: "12px",
                  outline: "none",
                  color: "#1a1a2e",
                }}
              />
              {showSuggestions && searchQuery.trim().length > 0 && (
                <div
                  className="search-suggestions-dropdown"
                  style={{
                    position: "absolute",
                    top: "48px",
                    left: 0,
                    right: 0,
                    backgroundColor: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "10px",
                    boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
                    maxHeight: "300px",
                    overflowY: "auto",
                    zIndex: 9999,
                    padding: "6px 0",
                    width: "calc(100vw - 32px)",
                    maxWidth: "350px",
                  }}
                >
                  {suggestions.length > 0 ? (
                    suggestions.map((product) => (
                      <div
                        key={product._id}
                        onClick={() => {
                          navigate(`/product/${product._id}`);
                          setSearchQuery("");
                          setShowSuggestions(false);
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          padding: "8px 12px",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        className="suggestion-item"
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#f8fafc")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor =
                            "transparent")
                        }
                      >
                        <img
                          src={
                            product.images?.[0] ||
                            "/images/products/placeholder.png"
                          }
                          alt={product.name}
                          style={{
                            width: "32px",
                            height: "32px",
                            objectFit: "contain",
                            borderRadius: "4px",
                          }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "/images/products/placeholder.png";
                          }}
                        />
                        <div
                          style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            textAlign: "left",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "13px",
                              fontWeight: "700",
                              color: "#1e293b",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "180px",
                            }}
                          >
                            {product.name}
                          </span>
                          <span style={{ fontSize: "10px", color: "#64748b" }}>
                            {product.subcategory_id?.name ||
                              product.category_id?.name ||
                              "Copper Product"}
                          </span>
                        </div>
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: "800",
                            color: "#0f172a",
                          }}
                        >
                          ₹{product.price?.toLocaleString("en-IN") || "0"}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div
                      style={{
                        padding: "12px",
                        color: "#64748b",
                        fontSize: "13px",
                        textAlign: "center",
                      }}
                    >
                      No matches found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Mobile Actions: Profile only */}
            <div
              style={{ display: "flex", alignItems: "center", gap: "12px" }}
              className="mobile-actions-wrapper"
            >
              <button
                onClick={() => setIsAccountOpen(true)}
                style={{
                  background: "none",
                  border: "none",
                  padding: "8px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                <AiOutlineUser style={{ fontSize: "26px", color: "#282c3f" }} />
                {isAuth && (
                  <span
                    style={{
                      position: "absolute",
                      bottom: "6px",
                      right: "6px",
                      width: "8px",
                      height: "8px",
                      background: "#4caf50",
                      border: "1.5px solid white",
                      borderRadius: "50%",
                    }}
                  />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── 4. Category Nav ── */}
      {/* <CategoryList isScrolled={isScrolled} /> */}
      <AccountModal
        isOpen={isAccountOpen}
        onClose={() => setIsAccountOpen(false)}
        name={name}
        onOpenOrders={() => setIsOrdersOpen(true)}
        onOpenCart={() => setIsCartOpen(true)}
        onOpenWishlist={() => setIsWishlistOpen(true)}
        onOpenSettings={() => setIsSettingsOpen(true)}
      />
      <WishlistModal
        isOpen={isWishlistOpen}
        onClose={() => setIsWishlistOpen(false)}
        onOpenCart={() => {
          setIsWishlistOpen(false);
          setIsCartOpen(true);
        }}
      />
      <CartModal
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        initialStep={cartInitialStep}
        onOpenOrders={() => setIsOrdersOpen(true)}
      />
      <OrdersModal
        isOpen={isOrdersOpen}
        onClose={() => setIsOrdersOpen(false)}
      />
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
      <AddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
      />
    </div>
  );
};

export default Header;
